import { useCallback, useRef } from 'react'

const INPUT_SAMPLE_RATE = 16000
const RING_BUFFER_LENGTH = INPUT_SAMPLE_RATE * 6

export const useAudioPlayback = () => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const ringBufferRef = useRef(new Float32Array(RING_BUFFER_LENGTH))
  const readIdxRef = useRef(0)
  const writeIdxRef = useRef(0)
  const availableRef = useRef(0)

  const ensureContext = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      return audioContextRef.current
    }

    const ctx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE })
    audioContextRef.current = ctx

    const ringBuffer = ringBufferRef.current
    const processor = ctx.createScriptProcessor(4096, 1, 1)

    processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const output = event.outputBuffer.getChannelData(0)
      for (let i = 0; i < output.length; i++) {
        if (availableRef.current > 0) {
          output[i] = ringBuffer[readIdxRef.current]
          readIdxRef.current = (readIdxRef.current + 1) % RING_BUFFER_LENGTH
          availableRef.current--
        } else {
          output[i] = 0
        }
      }
    }

    processor.connect(ctx.destination)
    processorRef.current = processor

    return ctx
  }, [])

  const playChunk = useCallback(
    (int16Data: Int16Array) => {
      const ctx = ensureContext()

      if (ctx.state === 'suspended') {
        ctx.resume()
      }

      const ringBuffer = ringBufferRef.current

      for (let i = 0; i < int16Data.length; i++) {
        ringBuffer[writeIdxRef.current] = int16Data[i] / 32768
        writeIdxRef.current = (writeIdxRef.current + 1) % RING_BUFFER_LENGTH

        if (availableRef.current < RING_BUFFER_LENGTH) {
          availableRef.current++
        } else {
          readIdxRef.current = (readIdxRef.current + 1) % RING_BUFFER_LENGTH
        }
      }
    },
    [ensureContext]
  )

  const stop = useCallback(() => {
    processorRef.current?.disconnect()
    processorRef.current = null

    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    readIdxRef.current = 0
    writeIdxRef.current = 0
    availableRef.current = 0
    ringBufferRef.current = new Float32Array(RING_BUFFER_LENGTH)
  }, [])

  return { playChunk, stop }
}
